// react
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

// material ui
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';

// api
import {
  useSearchBuildingQuery,
  useSearchTicketTopicsQuery,
} from '@/api/generated/daluxfm/daluxfm';
import { useGetV2LocationsByIdQuery } from '@/api/generated/recalc/endpoints/v2/locations';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

// relative
import { getDaluxBuildingsMatchingRemoniLocation } from './daluxTicketUtils';
import CreateDaluxTicketForm from './forms/CreateDaluxTicketForm';
import {
  isDaluxBuildingOption,
  isDaluxTopicOption,
} from './forms/daluxTicketForm.mappers';

type DaluxTicketDialogProps = {
  open: boolean;
  onClose: () => void;
  energyGroupName?: string | null;
  locationId?: number | null;
};

export default function DaluxTicketDialog({
  open,
  onClose,
  energyGroupName,
  locationId,
}: DaluxTicketDialogProps) {
  const { t } = useTranslation('dalux-integration');
  const selectedAccount = useSelector(getSelectedAccount);
  const { email } = useSessionAuth();
  const accountId = selectedAccount?.AccountId;
  const { data: topicsData, isLoading: topicsLoading } =
    useSearchTicketTopicsQuery({ limit: 100 }, { skip: !accountId || !open });
  const { data: buildingsData, isLoading: buildingsLoading } =
    useSearchBuildingQuery({ limit: 100 }, { skip: !accountId || !open });
  const { data: remoniLocation, isLoading: locationLoading } =
    useGetV2LocationsByIdQuery(
      { id: locationId as number },
      { skip: !locationId || !open },
    );

  const topics = (topicsData?.items ?? []).filter(isDaluxTopicOption);
  const buildingOptions = (buildingsData?.items ?? []).filter(
    isDaluxBuildingOption,
  );
  const matchingBuildings = remoniLocation
    ? getDaluxBuildingsMatchingRemoniLocation({
        daluxBuildings: buildingOptions,
        remoniLocation,
      })
    : [];
  const initialBuildingId = remoniLocation
    ? (matchingBuildings[0]?.data?.buildingId ?? null)
    : null;
  const isReady = !topicsLoading && !buildingsLoading && !locationLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ConfirmationNumberIcon color="primary" />
        {t('ticket.title')}
      </DialogTitle>
      <DialogContent>
        {open && isReady ? (
          <CreateDaluxTicketForm
            key={`${email ?? ''}:${locationId ?? 'none'}:${initialBuildingId ?? 'none'}`}
            email={email}
            topics={topics}
            buildingOptions={buildingOptions}
            initialBuildingId={initialBuildingId}
            energyGroupName={energyGroupName}
            remoniLocation={remoniLocation}
            onCancel={onClose}
            onSuccess={onClose}
          />
        ) : null}
        {open && !isReady ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
